<template>
  <div class="wrap">
    <Menu :menu="menu" />
    <v-toolbar flat color="white">
      <v-toolbar-title v-if="isBase">
        Базовые блоки
      </v-toolbar-title>
      <v-toolbar-title v-else>
        Список блоков лендинга # {{ getCurrentLandingId() }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="cyan darken-3"
        dark
        class="mb-2 dialog-btn"
        v-if="!isBase && isDeveloper"
        @click="getBaseBlocks()"
        >Импорт</v-btn
      >
      <v-dialog v-model="baseDialog" max-width="1240px" @close="close">
        <v-card>
          <v-card-title>
            <span class="headline"> Базовые блоки </span>
          </v-card-title>
          <v-card-text>
            <CardItem
              v-if="baseItems"
              :items="baseItems"
              :landing-id="baseLandingId"
              :has-click-action="true"
              :hide-actions="true"
              type="block"
              @cardClick="importItem"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialog" max-width="720px" @close="close">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-if="isDeveloper"
            color="primary"
            dark
            class="mb-2 dialog-btn"
            v-bind="attrs"
            v-on="on"
            >Создать</v-btn
          >
        </template>
        <v-card>
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>
          <v-card-text>
            <template>
              <v-container>
                <v-form ref="form">
                  <v-row>
                    <v-text-field
                      v-model="editedItem.name"
                      label="Название"
                      :error-messages="getErrors('name')"
                    ></v-text-field>
                  </v-row>
                  <v-row>
                    <v-text-field
                      v-model="editedItem.alias"
                      label="Ключ"
                      :error-messages="getErrors('alias')"
                    ></v-text-field>
                  </v-row>
                  <v-row>
                    <v-textarea
                      name="input-7-1"
                      label="Описание"
                      rows="2"
                      v-model="editedItem.description"
                      :error-messages="getErrors('description')"
                    >
                    </v-textarea>
                  </v-row>
                  <v-row>
                    <v-list-item-title
                      class="justify-space-between d-flex align-center"
                    >
                      Схема блока
                      <v-btn
                        class="ma-2"
                        small
                        fab
                        color="primary"
                        :href="global.schemaHelpUrl"
                        target="_blank"
                      >
                        <v-icon>mdi-help</v-icon>
                      </v-btn>
                    </v-list-item-title>
                    <v-alert
                      v-if="Object.keys(editedItem.json).length"
                      type="error"
                    >
                      Обратите внимание! Данные блока уже заполнены, любые
                      изменения в схеме могут привести к потере данных.
                    </v-alert>
                    <VJsonEditor
                      v-model="editedItem.schema"
                      :options="options"
                      :plus="false"
                      height="400px"
                      label="Схема блока"
                      @error="hasJsonError = true"
                      @input="hasJsonError = false"
                    >
                    </VJsonEditor>
                    <div class="red--text" v-html="getErrors('schema')"></div>
                  </v-row>
                </v-form>
              </v-container>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="close">Отмена</v-btn>
            <v-btn
              color="blue darken-1"
              :class="{ disabled: hasJsonError }"
              text
              @click="save"
              >Сохранить</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <template v-if="items.length">
      <CardItem
        :items="items"
        :type="'blocks'"
        :landingId="getCurrentLandingId()"
        :hide-actions="!isDeveloper"
        @editItem="editItem"
        @deleteItem="deleteItem"
      />
    </template>
    <template v-else>
      <v-card-text>
        Список блоков пуст
      </v-card-text>
    </template>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar" :multi-line="multiLine" color="red">
      {{ errorText }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { GLOBAL, API_URL_PREFIX } from "@/config/consts";
import Menu from "@/components/Menu.vue";
import CardItem from "@/components/CardItem";
import { mapGetters } from "vuex";
import VJsonEditor from "v-jsoneditor";
import errors from "@/mixins/errors";

export default {
  name: "Block",
  data() {
    return {
      hasJsonError: false,
      dialog: false,
      baseDialog: false,
      items: [],
      baseItems: [],
      editedIndex: -1,
      editedItem: {
        name: "",
        alias: "",
        description: "",
        schema: {},
        json: {}
      },
      defaultItem: {
        name: "",
        alias: "",
        description: "",
        schema: {},
        json: {}
      },
      multiLine: true,
      snackbar: false,
      errorText: "",
      global: GLOBAL,
      overlay: false,
      options: {
        mode: "code"
      }
    };
  },
  mixins: [errors],
  components: {
    Menu,
    CardItem,
    VJsonEditor
  },
  computed: {
    ...mapGetters(["getErrorText", "isDeveloper"]),
    formTitle() {
      return this.editedIndex === -1 ? "Новый блок" : "Редактировать блок";
    },
    baseLandingId() {
      return GLOBAL.baseLandingId;
    },
    isBase() {
      return this.getCurrentLandingId() === this.baseLandingId;
    },
    menu() {
      const menu = [
        { icon: "mdi-home", text: "На главную", link: "/" },
        {
          icon: "mdi-puzzle-plus",
          text: this.isBase ? "Базовые блоки" : "Конструктор блоков",
          link: `/blocks?landing_id=${this.getCurrentLandingId()}`
        },
        {
          icon: "mdi-text",
          text: this.isBase ? "Базовый менеджмент" : "Менеджмент",
          link: `/management?landing_id=${this.getCurrentLandingId()}`
        }
      ];
      if (!this.isBase) {
        menu.push({
          icon: "mdi-contacts",
          text: "Формы",
          link: `/forms?landing_id=${this.getCurrentLandingId()}`
        });
      }
      return menu;
    }
  },
  async created() {
    await this.initialize();
  },
  watch: {
    dialog(val) {
      val || this.close();
    }
  },
  methods: {
    getCurrentLandingId() {
      return this.$route.query.landing_id;
    },
    async initialize() {
      await this.getBlockList(this.getCurrentLandingId());
    },
    async getBaseBlocks() {
      await this.getBlockList(GLOBAL.baseLandingId, true);
      this.baseDialog = true;
    },
    async importItem(item) {
      if (!confirm("Импортировать блок?")) {
        return;
      }
      this.editedItem = item;
      await this.save();
      this.baseDialog = false;
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    async deleteItem(item) {
      if (confirm("Вы уверены, что хотите удалить этот элемент?")) {
        const index = this.items.indexOf(item);
        try {
          await this.$axios.delete(GLOBAL.apiUrl.blocks + item.id);
          this.items.splice(index, 1);
        } catch ({ response }) {
          this.snackbar = true;
          this.errorText = this.getErrorText(response);
        }
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.clearErrors();
      });
    },
    async save() {
      if (this.hasJsonError) {
        return;
      }
      const data = {
        name: this.editedItem.name,
        alias: this.editedItem.alias,
        description: this.editedItem.description,
        url: this.editedItem.url,
        landing: this.getCurrentLandingId(),
        schema: this.editedItem.schema
      };
      if (this.editedIndex > -1) {
        await this.saveCreated(data, this.items[this.editedIndex].id);
      } else {
        await this.saveNew(data);
      }
    },
    async saveCreated(saveData, id) {
      try {
        const { data } = await this.$axios.patch(
          GLOBAL.apiUrl.blocks + id,
          saveData
        );
        Object.assign(this.items[this.editedIndex], data);
        this.close();
      } catch ({ response }) {
        this.snackbar = true;
        this.errorText = this.getErrorText(response);
        this.setErrors(response.data);
      }
    },
    async saveNew(saveData) {
      try {
        const { data } = await this.$axios.post(GLOBAL.apiUrl.blocks, saveData);
        this.items.unshift(data);
        this.close();
      } catch ({ response }) {
        this.snackbar = true;
        this.errorText = this.getErrorText(response);
        this.setErrors(response.data);
      }
    },
    async getBlockList(id, isBase) {
      try {
        this.overlay = true;
        const { data } = await this.$axios.get(
          `${API_URL_PREFIX}/landings/${id}/blocks/`
        );
        if (isBase) {
          this.baseItems = data.list;
        } else {
          this.items = data.list;
        }
      } catch ({ response }) {
        this.snackbar = true;
        this.errorText = this.getErrorText(response);
      } finally {
        this.overlay = false;
      }
    }
  }
};
</script>

<style>
button.jsoneditor-format,
button.jsoneditor-compact,
button.jsoneditor-sort,
button.jsoneditor-transform,
.jsoneditor-menu a.jsoneditor-poweredBy {
  display: none;
}
button.disabled {
  cursor: not-allowed;
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  white-space: pre;
}
.quillWrapper {
  height: 100%;
}
.json-row .v-input {
  padding-top: 0;
}
.json-row .theme--light.v-card {
  background: #eeeeee;
}
.json-row .v-btn--fab.v-size--default {
  margin-bottom: 10px;
}
.v-dialog > .v-card > .v-card__title {
  margin-bottom: 15px;
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td:last-child {
  max-width: 200px;
}
.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td
  .v-btn.v-size--default {
  font-size: 14px;
  text-transform: none;
}
.json-row .theme--light.v-card .v-btn:not(.v-btn--round).v-size--default {
  padding: 0;
  min-width: 46px;
}
.bg-card {
  background: #5a9eed36;
}
.dialog-btn {
  margin-right: 1%;
}
</style>
